/**
 * AssignmentDraftRow — the second step of "Add assignment": the picked column
 * sits in a SET-shaped row (`column = [value] ✓ ×`) at the end of the list, so
 * the draft reads exactly like the assignment it becomes — the same inline
 * draft the WHERE "Add condition" flow uses. Enter applies, Escape cancels.
 *
 * The value input suggests as you type (`SqlValueInput`): the table's columns,
 * the engine's functions (those fitting the column's type first), NULL /
 * DEFAULT — listed as soon as the draft opens, `quickValues` first (NULL for
 * a nullable column, `EXCLUDED.col` in an ON CONFLICT DO UPDATE). The column's
 * type shows beside it and a text column's placeholder shows the quoting.
 */

import { useMemo, useState } from 'react';
import { Check, X } from 'lucide-react';

import { Button } from '../../../../../components/ui/button';
import { SqlValueInput } from '../../../common/components/SqlValueInput';
import { escapeClaimProps } from '../../../common/utils/escapeClaimTarget';
import type { Column } from '../../../../sql-resolver/types/schema';
import { vqeCopy } from '../../ux/vqeCopy';
import { columnValueSuggestions, isTextType } from '../../../common/utils/sqlValueSuggestions';
import type { WrittenTableColumns } from './assignmentColumns';

export interface AssignmentDraftRowProps {
  readonly testIdPrefix: string;
  /** The column as the row displays it. */
  readonly columnLabel: string;
  /** Catalog column when picked from the schema — drives the type hint + NULL. */
  readonly column: Column | null;
  readonly table: WrittenTableColumns;
  /** Listed first in the suggestions (`NULL`, `EXCLUDED.col`). */
  readonly quickValues: readonly string[];
  /** Commits `column = valueText`; resolves once the edit landed (the row then unmounts) or was refused. */
  readonly onApply: (valueText: string) => Promise<void>;
  readonly onCancel: () => void;
}

export function AssignmentDraftRow({
  testIdPrefix,
  columnLabel,
  column,
  table,
  quickValues,
  onApply,
  onCancel,
}: AssignmentDraftRowProps) {
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const ready = value.trim().length > 0 && !busy;
  const type = column ? (column.formattedType ?? column.dataType) : null;
  const suggestions = useMemo(() => columnValueSuggestions(table.engine, table.columns, column, quickValues), [table, column, quickValues]);
  const apply = (): void => {
    if (!ready) return;
    setBusy(true);
    // A refused edit keeps the draft (and what was typed) to fix and retry.
    void onApply(value.trim()).finally(() => setBusy(false));
  };

  return (
    <div className="flex w-full flex-col gap-1" data-testid={`${testIdPrefix}-add-assignment-draft`}>
      <div className="flex items-center gap-2 rounded-md border border-primary/60 bg-primary/5 py-0.5 pl-2 pr-1 shadow-sm">
        <span className="shrink-0 font-mono text-xs font-semibold" data-testid={`${testIdPrefix}-add-assignment-column`}>
          {columnLabel}
        </span>
        {type ? <span className="max-w-24 shrink-0 truncate text-[10px] text-muted-foreground">{type}</span> : null}
        <span aria-hidden className="text-muted-foreground/70">=</span>
        <SqlValueInput
          value={value}
          onValueChange={setValue}
          suggestions={suggestions}
          onSubmit={apply}
          onCancel={onCancel}
          // Text values need quotes — the one hint worth a type-specific placeholder.
          placeholder={isTextType(type) ? vqeCopy.dmlColumns.textExample : vqeCopy.dmlColumns.valueHint}
          aria-label={vqeCopy.dmlColumns.valueLabel(columnLabel)}
          autoFocus
          testId={`${testIdPrefix}-add-assignment-value`}
          // Nothing typed yet: the list shows right away, quick values first.
          openWhenEmpty
          trackEvent={'feature:vqe_value_suggestion_accept'}
          className="h-6 min-w-0 flex-1"
          // Escape cancels the draft only, never the compact popover around it.
          {...escapeClaimProps}
        />
        <Button
          type="button"
          size="sm"
          className="h-6 w-6 shrink-0 p-0"
          disabled={!ready}
          onClick={apply}
          aria-label={vqeCopy.actions.addAssignment}
          data-testid={`${testIdPrefix}-add-assignment-submit`}
        >
          <Check className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-6 w-6 shrink-0 p-0 text-muted-foreground"
          onClick={onCancel}
          aria-label="Cancel"
          data-testid={`${testIdPrefix}-add-assignment-cancel`}
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}
